import React, { useCallback } from 'react'
import log from 'loglevel'
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext'
import {
  SuggestedEvidenceRecord,
  GetOperationalizationRequest,
  SUGGESTION_TYPES,
} from '@trustero/trustero-api-web/lib/recommend/recommend_pb'
import { RecommendPromiseClient } from '@trustero/trustero-api-web/lib/recommend/recommend_grpc_web_pb'
import { useToggleSuggestions } from 'src/components/async/suggestions/useToggleSuggestions'
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons'
import { useAuthorizedGrpcClient } from 'src/adapter'
import {
  getDedupedSuggestionsObject,
  SuggestionsObject,
} from 'src/components/async/suggestions/suggestions.helpers'
import { SubsectionAccordionRefreshIcon } from 'src/pages/Controls/ControlsShowPage/ControlsShowItem/ControlsShowItemAccordions/ControlsShowItemGuidance/ControlsShowItemGuidance.components'
import { useAnalytics } from 'src/analytics/useAnalytics'
import { getSuggestionType } from 'src/pages/Controls/ControlsShowPage/ControlsShowItem/ControlsShowItemAccordions/ControlsShowItemGuidance/ControlsShowItemGuidance.helpers'
import { useRegenerateSuggestions } from 'src/components/async/suggestions/useSuggestedEvidence'
import isFunction from 'lodash/isFunction'
import {
  AccordionAnswerAgainBtn,
  AccordionDismissBtn,
  AnswerAgainContainer,
  DismissContainer,
  SmallStyledSubsectionAccordionIcon,
  StyledAccordionItem,
  SubsectionAccordionBody,
  SubsectionAccordionFooterContainer,
  SubsectionAccordionHeader,
  SubsectionAccordionHeaderGrid,
  SubSectionAccordionHeaderIconContainer,
  SubsectionAccordionHeaderNote,
  SubSectionAccordionHeaderTitleContainer,
} from './SubsectionAccordion.styles'

export const SubsectionAccordionDismissIcon = (): JSX.Element => {
  return <SmallStyledSubsectionAccordionIcon icon={faCircleXmark} />
}

export const SubsectionAccordionHeaderContent = ({
  icon,
  title,
  note,
  $customGridLayout,
  alignTop,
}: {
  icon: JSX.Element
  title: string | JSX.Element
  note?: string | JSX.Element
  $customGridLayout?: string
  alignTop?: boolean
}): JSX.Element => {
  return (
    <SubsectionAccordionHeaderGrid
      $customGridLayout={$customGridLayout}
      $alignTop={alignTop}
    >
      <SubSectionAccordionHeaderIconContainer>
        {icon}
      </SubSectionAccordionHeaderIconContainer>
      <SubSectionAccordionHeaderTitleContainer>
        {title}
      </SubSectionAccordionHeaderTitleContainer>
      {note && (
        <SubsectionAccordionHeaderNote>{note}</SubsectionAccordionHeaderNote>
      )}
    </SubsectionAccordionHeaderGrid>
  )
}

export const SubsectionAccordionGuidanceFooter = ({
  isActive,
  controlModelId,
  controlId,
  suggestionType,
  complianceFrameworkIds,
  canBeDismissed,
  revalidateSuggestion,
  updateDismissed,
  refresh,
  toggleLoading,
  suggestion,
}: {
  isActive: boolean
  controlModelId: string
  controlId: string
  suggestionType: SUGGESTION_TYPES
  complianceFrameworkIds?: string[]
  canBeDismissed?: boolean
  revalidateSuggestion?: () => void
  updateDismissed?: (type: SUGGESTION_TYPES, value: boolean) => void
  // For Tailored Guidance, SuggestedTests, SuggestedEvidence
  refresh?: (newGuidance: SuggestionsObject) => void
  toggleLoading?: (value: boolean) => void
  suggestion?: SuggestionsObject
}): JSX.Element => {
  const recommendClient = useAuthorizedGrpcClient(RecommendPromiseClient)
  const { dismissSuggestion } = useToggleSuggestions({
    controlId,
    suggType: suggestionType,
  })
  const { track, events } = useAnalytics()
  const regenerateSuggestions = useRegenerateSuggestions(controlId)
  const showAnswerAgain = suggestionType === SUGGESTION_TYPES.GUIDANCE
  const showResetToDefault =
    suggestionType === SUGGESTION_TYPES.TESTS ||
    suggestionType === SUGGESTION_TYPES.EVIDENCE
  const typeString = getSuggestionType(suggestionType)

  const onDismiss = async (e: React.MouseEvent): Promise<void> => {
    try {
      e.preventDefault()
      e.stopPropagation()
      await dismissSuggestion()
      revalidateSuggestion?.()
      updateDismissed?.(suggestionType, true)
      track(events.C_SUGG_DISMISS_GUIDANCE, {
        modelID: controlModelId,
        type: typeString,
        suggestion,
      })
    } catch (err) {
      log.error(`Failed to dismiss ${typeString} accordion:`, err)
    }
  }

  const regenerateSuggestionHandler = async () => {
    isFunction(toggleLoading) && toggleLoading(true)
    await regenerateSuggestions(suggestionType)
    isFunction(toggleLoading) && toggleLoading(false)
  }

  const refreshGuidance = useCallback(async () => {
    if (!showAnswerAgain) {
      return
    }
    try {
      toggleLoading?.(true)
      track(events.C_SUGG_REFRESH_GUIDANCE, {
        modelId: controlModelId,
        type: typeString,
        oldSuggestion: suggestion,
      })
      const response =
        await recommendClient.getSuggestedOperationalizationFromTable(
          new GetOperationalizationRequest()
            .setControlId(controlId)
            .setComplianceFrameworkBaseModelIdsList(
              complianceFrameworkIds || [],
            )
            .setRefresh(true),
        )
      const tailoredGuidanceObj = response.toObject().operationalization
      const tailoredGuidance = getDedupedSuggestionsObject(
        tailoredGuidanceObj
          ? ([tailoredGuidanceObj] as SuggestedEvidenceRecord.AsObject[])
          : [],
      )
      refresh?.(tailoredGuidance)
      revalidateSuggestion?.()
    } catch (err) {
      log.error(
        `Failed to generate tailored guidance for control ${controlModelId}`,
      )
    } finally {
      toggleLoading?.(false)
    }
  }, [
    showAnswerAgain,
    toggleLoading,
    track,
    events.C_SUGG_REFRESH_GUIDANCE,
    controlModelId,
    typeString,
    suggestion,
    recommendClient,
    controlId,
    complianceFrameworkIds,
    refresh,
    revalidateSuggestion,
  ])
  return !isActive ? (
    <></>
  ) : (
    <SubsectionAccordionFooterContainer>
      {showAnswerAgain && (
        <AnswerAgainContainer>
          <SubsectionAccordionRefreshIcon />
          <AccordionAnswerAgainBtn onClick={refreshGuidance}>
            Answer Again
          </AccordionAnswerAgainBtn>
        </AnswerAgainContainer>
      )}
      {showResetToDefault && (
        <AnswerAgainContainer>
          <SubsectionAccordionRefreshIcon />
          <AccordionAnswerAgainBtn onClick={regenerateSuggestionHandler}>
            Regenerate
          </AccordionAnswerAgainBtn>
        </AnswerAgainContainer>
      )}
      {canBeDismissed && (
        <DismissContainer>
          <SubsectionAccordionDismissIcon />
          <AccordionDismissBtn onClick={onDismiss}>Dismiss</AccordionDismissBtn>
        </DismissContainer>
      )}
    </SubsectionAccordionFooterContainer>
  )
}

export const SubsectionAccordionItemComponent = ({
  eventKey,
  icon,
  title,
  notes,
  body,
  customFooter,
  headerActions,
  handleHeaderClick,
  isDisabled,
  alwaysOpen,
  handleBodyClick,
  $customGridLayout,
  noBottomBorder,
  alignTop,
}: {
  eventKey: AccordionEventKey
  icon: JSX.Element
  title: string | JSX.Element
  notes?: string | JSX.Element
  body: JSX.Element
  customFooter?: JSX.Element
  headerActions?: JSX.Element
  handleHeaderClick?: (e: React.MouseEvent) => void
  isDisabled?: boolean
  alwaysOpen?: boolean
  handleBodyClick?: (e: React.MouseEvent) => void
  $customGridLayout?: string
  noBottomBorder?: boolean
  alignTop?: boolean
}): JSX.Element => (
  <StyledAccordionItem
    eventKey={eventKey as string}
    onClick={handleHeaderClick}
  >
    <SubsectionAccordionHeader
      $currentEventKey={eventKey}
      disabled={isDisabled}
      $noBottomBorder={noBottomBorder}
      $alignTop={alignTop}
      $headerActions={headerActions}
    >
      <SubsectionAccordionHeaderContent
        icon={icon}
        title={title}
        note={notes}
        $customGridLayout={$customGridLayout}
        alignTop={alignTop}
      />
    </SubsectionAccordionHeader>
    {(!isDisabled || alwaysOpen) && (
      <SubsectionAccordionBody
        onClick={handleBodyClick ? handleBodyClick : null}
      >
        {body}
      </SubsectionAccordionBody>
    )}
    {!!customFooter && customFooter}
  </StyledAccordionItem>
)
